import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation as useRouterLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateTimeIST, formatTimeIST } from "@/lib/formatUtils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, LogOut as CheckOutIcon, Phone, Printer, FileEdit } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import GatePassLabel from "@/components/shared/GatePassLabel";
import CameraCapture from "@/components/shared/CameraCapture";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import { VisitorForm, EmployeeForm } from "./people/EntryForms";
import { QrCheckoutBar, QrCheckoutDialog } from "./people/QrCheckout";
import OtherValueDialog from "@/components/shared/OtherValueDialog";

export default function PeopleMovement() {
  const routerState = useRouterLocation().state;
  const [tab, setTab] = useState(routerState?.tab || "visitor");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [masters, setMasters] = useState({ purposes: [], divisions: [], employees: [] });
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [showGatePass, setShowGatePass] = useState(false);
  const [gatePassVisitor, setGatePassVisitor] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const [qrInput, setQrInput] = useState("");
  const [qrLookupResult, setQrLookupResult] = useState(null);
  const [showQrCheckout, setShowQrCheckout] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [destFilter, setDestFilter] = useState("");
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  // Column definitions for ExportButtons
  const visitorColumns = useMemo(() => [
    { key: "ref_no", label: "Ref No" },
    { key: "gate_pass_no", label: "Gate Pass" },
    { key: "visitor_name", label: "Visitor Name" },
    { key: "mobile_no", label: "Mobile" },
    { key: "company", label: "Company" },
    { key: "purpose", label: "Purpose" },
    { key: "whom_to_meet", label: "Whom to Meet" },
    { key: "in_time", label: "In Time" },
    { key: "out_time", label: "Out Time" },
    { key: "status", label: "Status" },
  ], []);

  const employeeColumns = useMemo(() => [
    { key: "ref_no", label: "Ref No" },
    { key: "employee_name", label: "Employee Name" },
    { key: "employee_code", label: "Employee Code" },
    { key: "department", label: "Department" },
    { key: "purpose", label: "Purpose" },
    { key: "out_time", label: "Out Time" },
    { key: "expected_return", label: "Expected Return" },
    { key: "in_time", label: "In Time" },
    { key: "status", label: "Status" },
  ], []);

  const fetchEntries = useCallback(async () => {
    try {
      const params = { location, search: search || undefined };
      if (tab === "employee" && destFilter) params.destination = destFilter;
      const { data } = await api.get(`/mod1/${tab}`, { params });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error(err); }
  }, [tab, location, search, destFilter]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(`/drafts/${tab}`, { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [tab, location]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  const loadMasters = useCallback(async () => {
    try {
      const [p, d, e] = await Promise.all([
        api.get("/masters/purposes"), api.get("/masters/divisions"), api.get("/masters/employees"),
      ]);
      setMasters({ purposes: p.data.items, divisions: d.data.items, employees: e.data.items });
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  useEffect(() => {
    if (routerState?.openForm) { setForm({}); setCurrentDraftId(null); setShowForm(true); }
  }, [routerState]);

  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post(`/drafts/${tab}`, payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, tab, location]);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: formatFieldValue(key, val) }));

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId) api.post(`/drafts/${tab}/${currentDraftId}/release-lock`).catch(() => {});
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({});
  };

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post(`/drafts/${tab}`, payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save draft"); }
  };

  const handleSubmit = async () => {
    if (tab === "visitor") {
      if (!form.visitor_name?.trim()) { toast.error("Visitor Name is required"); return; }
      if (!form.company_from?.trim()) { toast.error("Company is required"); return; }
      if (!form.whom_to_meet?.trim()) { toast.error("Whom to Meet is required"); return; }
      if (!form.purpose?.trim()) { toast.error("Purpose is required"); return; }
    }
    if (tab === "employee") {
      if (!form.employee_name?.trim()) { toast.error("Employee Name is required"); return; }
      if (!form.department?.trim()) { toast.error("Department is required"); return; }
      if (!form.destination?.trim()) { toast.error("Destination is required"); return; }
    }
    setSaving(true);
    try {
      const payload = { ...formatFormData(form), location };
      // Resolve "Other" destination to the free-text value
      if (tab === "employee" && payload.destination === "Other" && payload.destination_other?.trim()) {
        payload.destination = payload.destination_other.trim();
      }
      delete payload.destination_other;
      const { data: entry } = await api.post(`/mod1/${tab}`, payload);
      toast.success("Entry created successfully");
      if (currentDraftId) await api.post(`/drafts/${tab}/${currentDraftId}/submit`).catch(() => {});
      if (tab === "visitor" && entry?.id) {
        try {
          const { data: gpData } = await api.post(`/gatepass/generate/${entry.id}`);
          setGatePassVisitor(gpData.gate_pass);
          setShowGatePass(true);
        } catch (err) { logger.error("Gate pass generation failed:", err); }
      }
      setShowForm(false);
      setForm({});
      setCurrentDraftId(null);
      setLastAutoSave(null);
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleCheckout = async (id) => {
    try {
      const endpoint = tab === "visitor" ? `visitor/${id}/checkout` : `employee/${id}/checkin`;
      await api.put(`/mod1/${endpoint}`);
      toast.success("Updated successfully");
      fetchEntries();
    } catch { toast.error("Action failed"); }
  };

  const handleSendOTP = async (id) => {
    try {
      const { data } = await api.post(`/mod1/visitor/${id}/send-otp`);
      toast.success(`OTP sent (Demo: ${data.debug_otp})`);
    } catch { toast.error("Failed to send OTP"); }
  };

  const handlePrintGatePass = async (visitorEntry) => {
    try {
      if (!visitorEntry.gate_pass_no) {
        const { data } = await api.post(`/gatepass/generate/${visitorEntry.id}`);
        setGatePassVisitor(data.gate_pass);
      } else {
        setGatePassVisitor(visitorEntry);
      }
      setShowGatePass(true);
    } catch { toast.error("Failed to generate gate pass"); }
  };

  const handleQrLookup = async () => {
    if (!qrInput.trim()) return;
    try {
      const params = qrInput.startsWith("GP-") ? { gp_no: qrInput.trim() } : { qr_data: qrInput.trim() };
      const { data } = await api.get("/gatepass/lookup", { params });
      setQrLookupResult(data.visitor);
      setShowQrCheckout(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gate pass not found");
      setQrLookupResult(null);
    }
  };

  const handleQrCheckout = async () => {
    if (!qrLookupResult?.id) return;
    try {
      await api.put(`/mod1/visitor/${qrLookupResult.id}/checkout`);
      toast.success(`${qrLookupResult.visitor_name} checked out`);
      setShowQrCheckout(false);
      setQrLookupResult(null);
      setQrInput("");
      fetchEntries();
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); }
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/${tab}/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await api.delete(`/drafts/${tab}/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch { toast.error("Failed"); }
  };

  const handlePhotoCapture = async (photoData) => {
    try {
      toast.info("Uploading photo...");
      const { data } = await api.post("/upload/photo", { photo: photoData });
      setForm(prev => ({ ...prev, photo: data.url }));
      toast.success("Photo uploaded");
    } catch {
      setForm(prev => ({ ...prev, photo: photoData }));
      toast.error("Cloud upload failed — photo saved locally");
    }
  };

  const fetchMasters = () => {
    Promise.all([
      api.get("/masters/purposes"), api.get("/masters/divisions"), api.get("/masters/employees"),
    ]).then(([p, d, e]) => {
      setMasters({ purposes: p.data.items, divisions: d.data.items, employees: e.data.items });
    }).catch((err) => { logger.error("Failed to refresh masters:", err); });
  };

  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    setField(otherField.fieldKey, val);
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null);
    setOtherValue("");
  };

  return (
    <div className="space-y-4 sm:space-y-5" data-testid="people-movement-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>People Movement</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-1 · Visitor & Employee tracking</p>
        </div>
        <Button onClick={() => { setForm({}); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> New Entry
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSearch(""); }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <TabsList className="h-10 sm:h-11 bg-[#1B3C87]/5">
            <TabsTrigger value="visitor" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-visitor">Visitor Entry</TabsTrigger>
            <TabsTrigger value="employee" className="h-8 sm:h-9 px-3 sm:px-5 text-[11px] sm:text-xs data-[state=active]:bg-[#1B3C87] data-[state=active]:text-white" data-testid="tab-employee">Employee In/Out</TabsTrigger>
          </TabsList>
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
          </div>
        </div>

        <div className="flex items-center mt-3">
          <ExportButtons
            reportType={tab === "visitor" ? "visitor_log" : "employee_movement"}
            title={tab === "visitor" ? "Visitor Log" : "Employee Movement"}
            entries={entries}
            columns={tab === "visitor" ? visitorColumns : employeeColumns}
            location={location}
          />
        </div>

        <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType={tab} />

        <TabsContent value="visitor">
          <QrCheckoutBar qrInput={qrInput} setQrInput={setQrInput} onLookup={handleQrLookup} />
          <Card className="mt-3">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref / GP No.", "Visitor", "Mobile", "Purpose", "Whom to Meet", "In Time", "Status", "Actions"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No entries found</TableCell></TableRow>}
                {entries.map((e) => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell>
                      <div className="font-mono text-xs">{e.ref_no}</div>
                      {e.gate_pass_no && <div className="text-[10px] text-[#1B3C87] font-bold">{e.gate_pass_no}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {e.photo && <img src={e.photo} alt="" className="w-7 h-7 rounded-full object-cover border" />}
                        <span className="font-medium">{e.visitor_name || "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{e.mobile_no || "—"}</TableCell>
                    <TableCell className="text-sm">{e.purpose || "—"}</TableCell>
                    <TableCell className="text-sm">{e.whom_to_meet || "—"}</TableCell>
                    <TableCell className="text-xs">{e.in_time ? formatDateTimeIST(e.in_time) : "—"}</TableCell>
                    <TableCell>
                      <span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status?.replace("_", " ")}</span>
                    </TableCell>
                    <TableCell onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex gap-1">
                        {e.status === "checked_in" && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => handlePrintGatePass(e)} data-testid={`print-gp-${e.id}`}>
                              <Printer size={12} />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => handleSendOTP(e.id)} data-testid={`send-otp-${e.id}`}>
                              <Phone size={12} />
                            </Button>
                            <Button size="sm" className="h-7 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleCheckout(e.id)} data-testid={`checkout-${e.id}`}>
                              <CheckOutIcon size={12} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>

        <TabsContent value="employee">
          {/* Destination filter for employee tab */}
          <div className="flex items-center gap-3 mt-3 mb-2">
            <Select value={destFilter} onValueChange={v => setDestFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-9 w-48" data-testid="dest-filter">
                <SelectValue placeholder="All Destinations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Destinations</SelectItem>
                <SelectItem value="Focal Point">Focal Point</SelectItem>
                <SelectItem value="Sahnewal">Sahnewal</SelectItem>
              </SelectContent>
            </Select>
            {destFilter && <span className="text-xs text-gray-500">Showing employees sent to <strong>{destFilter}</strong></span>}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
                  {["Ref No.", "Employee", "Department", "Purpose", "Destination", "Out Time", "Return Time", "Status", "Actions"].map(h => (
                    <TableHead key={h} className="text-white text-xs uppercase tracking-wider font-bold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No entries found</TableCell></TableRow>}
                {entries.map((e) => (
                  <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="font-medium">{e.employee_name || "—"}</TableCell>
                    <TableCell className="text-sm">{e.department || "—"}</TableCell>
                    <TableCell className="text-sm">{e.purpose || "—"}</TableCell>
                    <TableCell className="text-sm">{e.destination || "—"}</TableCell>
                    <TableCell className="text-xs">{e.out_time ? formatDateTimeIST(e.out_time) : "—"}</TableCell>
                    <TableCell className="text-xs">{e.in_time ? formatDateTimeIST(e.in_time) : "—"}</TableCell>
                    <TableCell><span className={`status-${e.status} px-2 py-0.5 rounded text-xs font-semibold`}>{e.status?.replace("_", " ")}</span></TableCell>
                    <TableCell onClick={(ev) => ev.stopPropagation()}>
                      {e.status === "out" && (
                        <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleCheckout(e.id)} data-testid={`return-emp-${e.id}`}>Return</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Entry Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : `New ${tab === "visitor" ? "Visitor" : "Employee"} Entry`}
              </DialogTitle>
              {lastAutoSave && (
                <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>
              )}
            </div>
          </DialogHeader>
          {tab === "visitor" ? (
            <VisitorForm form={form} setField={setField} masters={masters} onSelectOrOther={handleSelectOrOther} onOpenCamera={() => setShowCamera(true)} />
          ) : (
            <EmployeeForm form={form} setField={setField} masters={masters} onSelectOrOther={handleSelectOrOther} currentLocation={location} />
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GatePassLabel open={showGatePass} onClose={() => setShowGatePass(false)} visitor={gatePassVisitor} />
      <CameraCapture open={showCamera} onClose={() => setShowCamera(false)} onCapture={handlePhotoCapture} existingPhoto={form.photo} />

      {/* "Other" value input dialog */}
      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={handleOtherSubmit}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      <QrCheckoutDialog open={showQrCheckout} onClose={setShowQrCheckout} result={qrLookupResult} onCheckout={handleQrCheckout} />

      <EntryDetailDialog
        open={!!detailEntry}
        onClose={() => setDetailEntry(null)}
        entry={detailEntry}
        title={tab === "visitor" ? "Visitor Details" : "Employee Details"}
        collection={tab === "visitor" ? "visitors" : "employee_movements"}
        onRefresh={fetchEntries}
      />
    </div>
  );
}
